import { useCallback, useState } from "react";
import { restDelete, restPost } from "../../lib/restClient.js";
import { useMyPermissions } from "../../lib/permissions.js";
import { denialOrRawMessage } from "../../lib/denialReason.js";
import { complianceHealthPresentation } from "../../lib/complianceIntegrationHealth.js";
import ConfirmDialog from "../../components/ConfirmDialog.jsx";
import { useOptionalComplianceIntegrationHealth } from "../../components/ComplianceIntegrationHealth.jsx";
import { DIALOG } from "../../lib/analytics/surfaceVocabulary.js";
import { NO_DIRECTORY, keyIntegrationSteps, scopeCaptureStep } from "./model.js";
import {
    AsideSection,
    ErrorText,
    Field,
    Panel,
    SourceLoadState,
    SourcePage,
    Step,
    Steps,
    inputCls,
} from "../../components/setupSteps.jsx";
import { dangerBtnCls, primaryBtnCls, secondaryBtnCls } from "../../components/statusCard.jsx";

// A write-only key: the GET returns a status, never the secret, so a saved key
// is never round-tripped into an input.
const KEY_SOURCES = {
    anthropic: {
        route: "/v1/compliance-integration",
        dialog: DIALOG.ANTHROPIC_KEY_REMOVE,
        createTitle: "Create a Compliance Access Key in claude.ai",
        createBody: "Create a Compliance Access Key in claude.ai. It starts with sk-ant-api01-.",
        keyLabel: "Compliance Access Key",
        keyPlaceholder: "sk-ant-api01-...",
        does: [
            "Reads your organization's claude.ai and Claude Desktop chats through Anthropic's Compliance API.",
            "Shows them alongside the prompts captured on endpoints.",
        ],
    },
};

export default function KeySourcePage({ source, health, entry, directory, onBack, onChanged }) {
    const config = KEY_SOURCES[source.key];
    const caps = useMyPermissions();
    const integrationHealth = useOptionalComplianceIntegrationHealth();
    const summary = entry?.data;
    const [apiKey, setApiKey] = useState("");
    const [replacing, setReplacing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [confirmRemove, setConfirmRemove] = useState(false);

    const refreshHealth = integrationHealth?.refresh;

    const reloadAfterMutation = useCallback(() => Promise.all([
        onChanged(),
        source.key === "anthropic" ? refreshHealth?.({ afterCurrent: true }) : undefined,
    ]), [onChanged, refreshHealth, source.key]);

    const save = useCallback(async () => {
        setSaving(true);
        setError(null);
        try {
            await restPost(`${config.route}:set-key`, { api_key: apiKey.trim() });
            setApiKey("");
            setReplacing(false);
            await reloadAfterMutation();
        } catch (err) {
            setError(denialOrRawMessage(err, caps));
        } finally {
            setSaving(false);
        }
    }, [apiKey, caps, config, reloadAfterMutation]);

    const remove = useCallback(async () => {
        setConfirmRemove(false);
        setSaving(true);
        setError(null);
        try {
            await restDelete(config.route);
            setApiKey("");
            setReplacing(false);
            await reloadAfterMutation();
        } catch (err) {
            setError(denialOrRawMessage(err, caps));
        } finally {
            setSaving(false);
        }
    }, [caps, config, reloadAfterMutation]);

    if (!summary) {
        return (
            <SourcePage source={source} health={health} onBack={onBack} backLabel="Back to data sources" busy={!entry?.loaded || entry?.loading} aside={null}>
                <SourceLoadState entry={entry} onRetry={onChanged} />
            </SourcePage>
        );
    }

    const configured = summary.configured;
    const {
        states: [, keyState, syncState],
        noDirectory,
    } = scopeCaptureStep(keyIntegrationSteps(summary, source.key), directory);
    // Before anything is saved, step 1 counts as done once the admin has a key
    // to paste, which moves "next step" onto the form.
    const typed = apiKey.trim().length > 0;
    const createState = configured || typed ? "done" : "current";
    const pasteState = replacing || (!configured && typed) ? "current" : configured ? keyState : "todo";
    const showForm = !configured || replacing;
    const canSave = apiKey.trim().length > 0 && !saving;

    return (
        <SourcePage
            source={source}
            health={health}
            onBack={onBack}
            backLabel="Back to data sources"
            busy={entry.loading}
            aside={
                <>
                    <AsideSection title="What Origin does">
                        {config.does.map((line) => (
                            <p key={line} className="m-0">
                                {line}
                            </p>
                        ))}
                    </AsideSection>
                    <AsideSection title="Key storage">
                        <p className="m-0">Stored encrypted. Origin never shows it again after saving.</p>
                    </AsideSection>
                    {configured ? (
                        <AsideSection title="Remove source">
                            <p className="m-0">Capture stops. Chats already imported are kept.</p>
                            <button
                                type="button"
                                onClick={() => setConfirmRemove(true)}
                                disabled={saving}
                                className={`${dangerBtnCls} w-fit`}
                            >
                                Remove
                            </button>
                        </AsideSection>
                    ) : null}
                </>
            }
        >
            <Steps label={`${source.name} setup`}>
                <Step
                    number={1}
                    state={createState}
                    title={config.createTitle}
                    summary={configured ? "Done." : config.createBody}
                />
                <Step
                    number={2}
                    state={pasteState}
                    title={configured ? "Key saved" : "Paste the key"}
                    summary={configured ? "Stored encrypted." : null}
                    action={
                        configured && !replacing ? (
                            <button type="button" onClick={() => setReplacing(true)} className={secondaryBtnCls}>
                                Replace key
                            </button>
                        ) : null
                    }
                >
                    {showForm ? (
                        <Panel className="grid gap-[12px] p-[14px]">
                            <Field label={config.keyLabel}>
                                <input
                                    type="password"
                                    value={apiKey}
                                    autoComplete="off"
                                    onChange={(event) => setApiKey(event.target.value)}
                                    placeholder={config.keyPlaceholder}
                                    className={inputCls}
                                />
                            </Field>
                            <div className="flex flex-wrap items-center gap-[8px]">
                                <button type="button" onClick={save} disabled={!canSave} className={primaryBtnCls}>
                                    {saving ? "Saving..." : configured ? "Replace key" : "Save key"}
                                </button>
                                {replacing ? (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setReplacing(false);
                                            setApiKey("");
                                            setError(null);
                                        }}
                                        className={secondaryBtnCls}
                                    >
                                        Cancel
                                    </button>
                                ) : null}
                            </div>
                            <ErrorText>{error}</ErrorText>
                        </Panel>
                    ) : (
                        <ErrorText>{error}</ErrorText>
                    )}
                </Step>
                <Step
                    number={3}
                    state={syncState}
                    title={
                        noDirectory ? NO_DIRECTORY.label : syncState === "done" ? "Capturing" : "Confirm the first sync"
                    }
                    summary={noDirectory ? NO_DIRECTORY.detail : complianceHealthPresentation(summary).impact}
                    action={
                        configured && syncState !== "done" && !noDirectory ? (
                            <button
                                type="button"
                                onClick={onChanged}
                                disabled={entry.loading}
                                className={secondaryBtnCls}
                            >
                                {entry.loading ? "Checking..." : "Refresh"}
                            </button>
                        ) : null
                    }
                    last
                />
            </Steps>
            <ConfirmDialog
                open={confirmRemove}
                analyticsId={config.dialog}
                eyebrow={source.name}
                title={`Remove the ${source.name} key?`}
                body="Capture stops immediately. Chats already imported are kept. Reconnecting means pasting a key again."
                confirmLabel="Remove key"
                destructive
                onConfirm={remove}
                onCancel={() => setConfirmRemove(false)}
            />
        </SourcePage>
    );
}
